import { useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Eye, RotateCcw } from 'lucide-react';
import {
  listEmailTemplates,
  previewEmailTemplate,
  resetEmailTemplate,
  saveEmailTemplate,
  type EmailTemplate,
  type EmailTemplateKey,
} from '../../lib/emailTemplatesApi';
import { useI18n } from '../../contexts/I18nContext';
import { Select } from '../ui/Select';

// Settings → Email templates. Templates live in code server-side; this screen
// edits the per-key DB override ({{variable}} substitution documented via the
// chips), previews the draft with sample data in a sandboxed iframe and can
// reset a customized template back to the code default.

const TEMPLATES_KEY = ['email-templates'] as const;

export function EmailTemplatesSettings() {
  const { t } = useI18n();
  const qc = useQueryClient();

  const query = useQuery({ queryKey: TEMPLATES_KEY, queryFn: listEmailTemplates });

  const [selectedKey, setSelectedKey] = useState<EmailTemplateKey>('overdue_invoice');
  // Drafts: null = untouched (show the server-resolved value).
  const [draftSubject, setDraftSubject] = useState<string | null>(null);
  const [draftBody, setDraftBody] = useState<string | null>(null);
  const [status, setStatus] = useState<'idle' | 'saving' | 'done' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const bodyRef = useRef<HTMLTextAreaElement | null>(null);

  const templates = query.data ?? [];
  const template = templates.find((tpl) => tpl.key === selectedKey) ?? null;
  const subject = draftSubject ?? template?.subject ?? '';
  const bodyHtml = draftBody ?? template?.bodyHtml ?? '';
  const dirty = draftSubject !== null || draftBody !== null;

  function selectTemplate(key: string) {
    setSelectedKey(key as EmailTemplateKey);
    setDraftSubject(null);
    setDraftBody(null);
    setPreviewHtml(null);
    setStatus('idle');
    setError(null);
  }

  function markEdited() {
    setStatus('idle');
    setPreviewHtml(null);
  }

  /** Insert `{{name}}` at the caret of the body textarea. */
  function insertVariable(name: string) {
    const token = `{{${name}}}`;
    const el = bodyRef.current;
    if (!el) return;
    const start = el.selectionStart ?? bodyHtml.length;
    const end = el.selectionEnd ?? bodyHtml.length;
    const next = bodyHtml.slice(0, start) + token + bodyHtml.slice(end);
    setDraftBody(next);
    markEdited();
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(start + token.length, start + token.length);
    });
  }

  function applySaved(saved: EmailTemplate) {
    qc.setQueryData<EmailTemplate[]>(TEMPLATES_KEY, (prev) =>
      (prev ?? []).map((tpl) => (tpl.key === saved.key ? saved : tpl)),
    );
    setDraftSubject(null);
    setDraftBody(null);
    setPreviewHtml(null);
  }

  async function handleSave() {
    if (!template || subject.trim().length === 0 || bodyHtml.trim().length === 0) return;
    setStatus('saving');
    setError(null);
    try {
      applySaved(await saveEmailTemplate(template.key, { subject: subject.trim(), bodyHtml }));
      setStatus('done');
    } catch (e) {
      setError(e instanceof Error ? e.message : t('settings.unknownError'));
      setStatus('error');
    }
  }

  async function handleReset() {
    if (!template) return;
    setStatus('saving');
    setError(null);
    try {
      applySaved(await resetEmailTemplate(template.key));
      setStatus('done');
    } catch (e) {
      setError(e instanceof Error ? e.message : t('settings.unknownError'));
      setStatus('error');
    }
  }

  async function handlePreview() {
    if (!template || previewLoading) return;
    setPreviewLoading(true);
    setError(null);
    try {
      const res = await previewEmailTemplate(template.key, { subject, bodyHtml });
      setPreviewHtml(res.html);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('settings.unknownError'));
      setStatus('error');
    } finally {
      setPreviewLoading(false);
    }
  }

  return (
    <div className="max-w-3xl">
      <p className="text-sm text-gray-500">{t('settings.emailTemplates.intro')}</p>

      {query.isLoading && (
        <p className="mt-6 text-sm text-gray-500">{t('settings.emailTemplates.loading')}</p>
      )}
      {query.isError && (
        <p className="mt-6 text-sm text-red-600">
          {t('settings.emailTemplates.loadError')}
          {query.error instanceof Error ? `: ${query.error.message}` : ''}.
        </p>
      )}

      {template && (
        <div className="mt-6 rounded-lg border border-gray-200 bg-white p-5">
          <div className="flex flex-wrap items-end gap-3">
            <div className="w-64">
              <Select
                label={t('settings.emailTemplates.template')}
                value={selectedKey}
                onChange={selectTemplate}
                options={templates.map((tpl) => ({
                  value: tpl.key,
                  label: t(`settings.emailTemplates.name.${tpl.key}`),
                }))}
              />
            </div>
            {template.isCustomized ? (
              <span className="mb-2 rounded-full bg-brand-50 px-2 py-0.5 text-xs font-medium text-brand-700">
                {t('settings.emailTemplates.customized')}
              </span>
            ) : (
              <span className="mb-2 rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-500">
                {t('settings.emailTemplates.default')}
              </span>
            )}
          </div>

          {/* Subject */}
          <label className="mt-4 block text-sm font-medium text-gray-700">
            {t('settings.emailTemplates.subject')}
            <input
              type="text"
              value={subject}
              onChange={(e) => {
                setDraftSubject(e.target.value);
                markEdited();
              }}
              maxLength={200}
              className="ui-input mt-1"
            />
          </label>

          {/* Variable chips (click-to-insert into the body) */}
          <div className="mt-4">
            <span className="block text-sm font-medium text-gray-700">
              {t('settings.emailTemplates.variables')}
            </span>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {template.variables.map((v) => (
                <button
                  key={v.name}
                  type="button"
                  onClick={() => insertVariable(v.name)}
                  title={t('settings.emailTemplates.insertVariable')}
                  className="rounded-full border border-gray-200 bg-gray-50 px-2 py-0.5 font-mono text-xs text-gray-700 transition hover:border-brand-300 hover:bg-brand-50 hover:text-brand-700"
                >
                  {`{{${v.name}}}`}
                </button>
              ))}
            </div>
            <p className="mt-1 text-xs text-gray-500">
              {t('settings.emailTemplates.variablesHint')}
            </p>
          </div>

          {/* Body */}
          <label className="mt-4 block text-sm font-medium text-gray-700">
            {t('settings.emailTemplates.body')}
            <textarea
              ref={bodyRef}
              value={bodyHtml}
              onChange={(e) => {
                setDraftBody(e.target.value);
                markEdited();
              }}
              rows={18}
              spellCheck={false}
              className="ui-textarea mt-1 resize-y font-mono text-xs"
            />
          </label>

          {/* Actions */}
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={handleSave}
              disabled={
                status === 'saving' ||
                !dirty ||
                subject.trim().length === 0 ||
                bodyHtml.trim().length === 0
              }
              className="ui-btn-primary"
            >
              {status === 'saving'
                ? t('settings.emailTemplates.saving')
                : t('settings.emailTemplates.save')}
            </button>
            <button
              type="button"
              onClick={handlePreview}
              disabled={previewLoading}
              className="ui-btn-secondary flex items-center gap-1.5"
            >
              <Eye size={14} aria-hidden />
              {previewLoading
                ? t('settings.emailTemplates.previewLoading')
                : t('settings.emailTemplates.preview')}
            </button>
            {template.isCustomized && (
              <button
                type="button"
                onClick={handleReset}
                disabled={status === 'saving'}
                className="ui-btn-secondary flex items-center gap-1.5 text-red-600"
              >
                <RotateCcw size={14} aria-hidden />
                {t('settings.emailTemplates.reset')}
              </button>
            )}
          </div>

          {status === 'done' && (
            <p className="mt-3 rounded-md border border-emerald-500/30 bg-emerald-500/10 p-3 text-sm text-emerald-600">
              {t('settings.emailTemplates.saved')}
            </p>
          )}
          {status === 'error' && (
            <p className="mt-3 rounded-md border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-600">
              {t('settings.emailTemplates.saveError')}
              {error ? `: ${error}` : ''}.
            </p>
          )}

          {previewHtml !== null && (
            <div className="mt-4">
              <span className="block text-sm font-medium text-gray-700">
                {t('settings.emailTemplates.previewTitle')}
              </span>
              <iframe
                title={t('settings.emailTemplates.previewTitle')}
                srcDoc={previewHtml}
                sandbox=""
                className="mt-1 h-[480px] w-full rounded-lg border border-gray-200 bg-white"
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
